import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  LinearProgress,
  Tooltip,
  Avatar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import GroupIcon from '@mui/icons-material/Group';
import StarIcon from '@mui/icons-material/Star';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import SchoolIcon from '@mui/icons-material/School';
import AssessmentIcon from '@mui/icons-material/Assessment';

const StatCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: theme.spacing(2),
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.15)',
  }
}));

const StatValue = styled(Typography)(({ theme }) => ({
  fontSize: '2.5rem',
  fontWeight: 'bold',
  lineHeight: 1,
  marginBottom: theme.spacing(0.5),
}));

const StatLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 500,
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
}));

const TrendIcon = ({ value, baseline = 70 }) => {
  if (value > baseline + 2) {
    return <TrendingUpIcon sx={{ color: 'success.main', fontSize: '1.2rem' }} />;
  } else if (value < baseline - 2) {
    return <TrendingDownIcon sx={{ color: 'error.main', fontSize: '1.2rem' }} />;
  }
  return <TrendingFlatIcon sx={{ color: 'info.main', fontSize: '1.2rem' }} />;
};

const ProgressBar = ({ value, max = 100, color = 'primary' }) => (
  <Box sx={{ width: '100%', mt: 1 }}>
    <LinearProgress
      variant="determinate"
      value={(value / max) * 100}
      sx={{
        height: 8,
        borderRadius: 4,
        bgcolor: 'rgba(0,0,0,0.1)',
        '& .MuiLinearProgress-bar': {
          borderRadius: 4,
        },
      }}
      color={color}
    />
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
      <Typography variant="caption" color="text.secondary">
        {value.toFixed(1)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {max}
      </Typography>
    </Box>
  </Box>
);

const FacultyStatsCard = ({ data, academicYear }) => {
  if (!data) return null;

  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 70) return 'warning';
    return 'error';
  };

  const getCompletionColor = (rate) => {
    if (rate >= 95) return 'success';
    if (rate >= 90) return 'warning';
    return 'error';
  };

  return (
    <Box>
      <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
        📊 สถิติรวมคณะวิศวกรรมศาสตร์
      </Typography>
      
      <Grid container spacing={3}>
        {/* Total Students */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Avatar 
                sx={{ 
                  bgcolor: 'primary.main', 
                  mb: 2, 
                  mx: 'auto',
                  width: 56,
                  height: 56
                }}
              >
                <GroupIcon fontSize="large" />
              </Avatar>
              <StatValue color="primary.main">
                {data.total_students?.toLocaleString() || '0'}
              </StatValue>
              <StatLabel>นิสิตฝึกงานทั้งหมด</StatLabel>
              <Chip 
                label={`ปี ${academicYear}`}
                size="small"
                color="primary"
                sx={{ mt: 1 }}
              />
            </CardContent>
          </StatCard>
        </Grid>

        {/* Average Score */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Avatar 
                sx={{ 
                  bgcolor: getScoreColor(data.avg_total_score).main || 'warning.main', 
                  mb: 2, 
                  mx: 'auto',
                  width: 56,
                  height: 56
                }}
              >
                <StarIcon fontSize="large" />
              </Avatar>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                <StatValue sx={{ color: `${getScoreColor(data.avg_total_score)}.main` }}>
                  {data.avg_total_score?.toFixed(1) || '0.0'}
                </StatValue>
                <TrendIcon value={data.avg_total_score} />
              </Box>
              <StatLabel>คะแนนเฉลี่ยรวม</StatLabel>
              <ProgressBar 
                value={data.avg_total_score} 
                max={100} 
                color={getScoreColor(data.avg_total_score)}
              />
            </CardContent>
          </StatCard>
        </Grid>

        {/* Companies */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Avatar 
                sx={{ 
                  bgcolor: 'info.main', 
                  mb: 2, 
                  mx: 'auto',
                  width: 56,
                  height: 56
                }}
              >
                <BusinessCenterIcon fontSize="large" />
              </Avatar>
              <StatValue color="info.main">
                {data.total_companies?.toLocaleString() || '0'}
              </StatValue>
              <StatLabel>บริษัทพาร์ทเนอร์</StatLabel>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {(data.total_students / data.total_companies).toFixed(1)} นิสิต/บริษัท
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>

        {/* Completion Rate */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Avatar 
                sx={{ 
                  bgcolor: getCompletionColor(data.completion_rate).main || 'success.main', 
                  mb: 2, 
                  mx: 'auto',
                  width: 56,
                  height: 56
                }}
              >
                <SchoolIcon fontSize="large" />
              </Avatar>
              <StatValue sx={{ color: `${getCompletionColor(data.completion_rate)}.main` }}>
                {data.completion_rate?.toFixed(1) || '0.0'}%
              </StatValue>
              <StatLabel>อัตราสำเร็จ</StatLabel>
              <ProgressBar 
                value={data.completion_rate} 
                max={100} 
                color={getCompletionColor(data.completion_rate)}
              />
            </CardContent>
          </StatCard>
        </Grid>

        {/* Additional Statistics Row */}
        <Grid item xs={12} sm={6} md={4}>
          <StatCard>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AssessmentIcon color="secondary" sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  การประเมิน 15 ข้อ
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                  {data.avg_evaluation_15?.toFixed(1) || '0.0'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  / 100
                </Typography>
              </Box>
              <ProgressBar 
                value={data.avg_evaluation_15} 
                max={100} 
                color="secondary"
              />
            </CardContent>
          </StatCard>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <StatCard>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <StarIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  ความพึงพอใจ
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                  {data.avg_satisfaction?.toFixed(1) || '0.0'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  / 10
                </Typography>
              </Box>
              <ProgressBar 
                value={data.avg_satisfaction} 
                max={10} 
                color="warning"
              />
            </CardContent>
          </StatCard>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <StatCard>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUpIcon color="info" sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  ส่วนเบียงเบนมาตรฐาน
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                  ±{data.std_deviation?.toFixed(1) || '0.0'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  คะแนน
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                การกระจายของคะแนนในคณะ
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FacultyStatsCard;
